import React, { useState, useEffect } from 'react';
import {
  SaveOutlined,
  RollbackOutlined,
  ClearOutlined,
  CarOutlined,
  UserOutlined,
  NumberOutlined,
  FileTextOutlined,
  CalendarOutlined,
  CloudUploadOutlined,
  WarningOutlined
} from '@ant-design/icons';
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Card,
  Typography,
  Space,
  message,
  Row,
  Col,
  Upload,
  Alert,
  Tag,
  Spin
} from 'antd';
import dayjs from 'dayjs';
import { getParties, createVehicleInsurance, getVehicleInsuranceById, updateVehicleInsurance, deleteVehicleInsurance } from '../Services/Services';

const { Title, Text } = Typography;
const { Option } = Select;

const NewInsurance = ({ onBack, editMode, vehicleNo }) => {
  const [loading, setLoading] = useState(false);
  const [parties, setParties] = useState([]);
  const [fetchingData, setFetchingData] = useState(false);
  const [originalVehicleNo, setOriginalVehicleNo] = useState('');
  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    vehicleNo: '',
    partyId: '',
    chassisNumber: '',
    taxValidity: null,
    fitnessValidity: null,
    npValidity: null,
    cgpValidity: null,
    insuranceValidity: null,
    pollutionValidity: null
  });

  const [files, setFiles] = useState({
    rcBook: null,
    insuranceDoc: null,
    pollutionDoc: null,
    trBook: null,
    permitDoc: null,
    fitnessDoc: null
  });

  const [existingFiles, setExistingFiles] = useState({
    rcBook: '',
    insuranceDoc: '',
    pollutionDoc: '',
    trBook: '',
    permitDoc: '',
    fitnessDoc: ''
  });

  useEffect(() => {
    loadParties();
  }, []);

  useEffect(() => {
    if (editMode && vehicleNo) {
      fetchVehicleData();
    }
  }, [editMode, vehicleNo]);

  const loadParties = async () => {
    setLoading(true);
    const data = await getParties();
    if (data) setParties(data);
    setLoading(false);
  };

  const fetchVehicleData = async () => {
    setFetchingData(true);
    try {
      const data = await getVehicleInsuranceById(vehicleNo);
      if (data) {
        const formValues = {
          vehicleNo: data.vehicleNo || '',
          partyId: data.partyId || data.party?.id,
          chassisNumber: data.chassisNumber || '',
          taxValidity: data.taxValidity ? dayjs(data.taxValidity) : null,
          fitnessValidity: data.fitnessValidity ? dayjs(data.fitnessValidity) : null,
          npValidity: data.npValidity ? dayjs(data.npValidity) : null,
          cgpValidity: data.cgpValidity ? dayjs(data.cgpValidity) : null,
          insuranceValidity: data.insuranceValidity ? dayjs(data.insuranceValidity) : null,
          pollutionValidity: data.pollutionValidity ? dayjs(data.pollutionValidity) : null
        };
        setFormData(formValues);
        form.setFieldsValue(formValues);
        setOriginalVehicleNo(data.vehicleNo || '');
        setExistingFiles({
          rcBook: data.rc_book || '',
          insuranceDoc: data.insurance_pdf || '',
          pollutionDoc: data.pollution_pdf || '',
          trBook: data.tr_book || '',
          permitDoc: data.permit_doc || '',
          fitnessDoc: data.fitness_doc || ''
        });
      }
    } catch (error) {
      message.error("Vehicle data could not be loaded!");
    } finally {
      setFetchingData(false);
    }
  };

  const handlePartyChange = (value) => {
    setFormData({ ...formData, partyId: value });
    form.setFieldsValue({ partyId: value });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    form.setFieldsValue({ [name]: value });
  };

  const handleFileChange = (field, file) => {
    if (file.file?.status === 'removed') {
      setFiles({ ...files, [field]: null });
      return false;
    }
    setFiles({ ...files, [field]: file.file });
    return false;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!values.vehicleNo) {
        message.error('Vehicle number is required');
        return;
      }
      if (!values.partyId) {
        message.error('Please select a party');
        return;
      }

      if (editMode && originalVehicleNo && values.vehicleNo !== originalVehicleNo) {
        const confirmChange = window.confirm(
          `Warning: Changing vehicle number from ${originalVehicleNo} to ${values.vehicleNo}.\n\nContinue?`
        );
        if (!confirmChange) return;
      }

      setLoading(true);

      const dataToSend = new FormData();
      dataToSend.append('vehicleNo', values.vehicleNo);
      dataToSend.append('partyId', values.partyId);
      dataToSend.append('chassisNumber', values.chassisNumber || '');

      if (values.taxValidity) dataToSend.append('taxValidity', values.taxValidity.format('YYYY-MM-DD'));
      if (values.fitnessValidity) dataToSend.append('fitnessValidity', values.fitnessValidity.format('YYYY-MM-DD'));
      if (values.npValidity) dataToSend.append('npValidity', values.npValidity.format('YYYY-MM-DD'));
      if (values.cgpValidity) dataToSend.append('cgpValidity', values.cgpValidity.format('YYYY-MM-DD'));
      if (values.insuranceValidity) dataToSend.append('insuranceValidity', values.insuranceValidity.format('YYYY-MM-DD'));
      if (values.pollutionValidity) dataToSend.append('pollutionValidity', values.pollutionValidity.format('YYYY-MM-DD'));

      if (files.rcBook) dataToSend.append('rc_book', files.rcBook);
      if (files.insuranceDoc) dataToSend.append('insurance_pdf', files.insuranceDoc);
      if (files.pollutionDoc) dataToSend.append('pollution_pdf', files.pollutionDoc);
      if (files.trBook) dataToSend.append('tr_book', files.trBook);
      if (files.permitDoc) dataToSend.append('permit_doc', files.permitDoc);
      if (files.fitnessDoc) dataToSend.append('fitness_doc', files.fitnessDoc);

      let response;

      if (editMode) {

        response = await updateVehicleInsurance(vehicleNo, dataToSend);

      } else {
        response = await createVehicleInsurance(dataToSend);
      }

      if (response && (response.status === 200 || response.status === 201 || response.id || response.vehicleNo)) {
        message.success(editMode ? 'Vehicle Updated!' : 'Data Saved!');
        if (!editMode) {
          form.resetFields();
          setFiles({
            rcBook: null, insuranceDoc: null, pollutionDoc: null,
            trBook: null, permitDoc: null, fitnessDoc: null
          });
        } else {
          setTimeout(() => onBack(), 1500);
        }
      }
    } catch (error) {
      message.error(editMode ? "Update failed!" : "Save failed!");
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    form.resetFields();
    setFormData({
      vehicleNo: '', partyId: '', chassisNumber: '',
      taxValidity: null, fitnessValidity: null, npValidity: null,
      cgpValidity: null, insuranceValidity: null, pollutionValidity: null
    });
    setFiles({
      rcBook: null, insuranceDoc: null, pollutionDoc: null,
      trBook: null, permitDoc: null, fitnessDoc: null
    });
    message.info("Form cleared");
  };

  const getPartyName = () => {
    const selectedParty = parties.find(p => p.id === parseInt(formData.partyId));
    return selectedParty ? selectedParty.partyName : '';
  };

  if (fetchingData) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading...</div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh' }}>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* Basic Information - Compact */}
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="partyId" label="Party" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                <Select
                  placeholder="Select Party"
                  onChange={handlePartyChange}
                  showSearch
                  size="middle"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toString()?.toLowerCase() ?? '').includes(input.toLowerCase())
                  }
                >
                  {parties.map((party) => (
                    <Option key={party.id} value={party.id}>
                      {party.partyName} {party.companyName ? `(${party.companyName})` : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item name="vehicleNo" label="Vehicle No" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                <Input placeholder="CG04 AB 1234" prefix={<CarOutlined />} onChange={handleInputChange} />
              </Form.Item>
              {editMode && originalVehicleNo !== formData.vehicleNo && formData.vehicleNo && (
                <Alert message={`Changed from ${originalVehicleNo}`} type="warning" showIcon style={{ marginTop: 4, padding: '4px 8px' }} />
              )}
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item name="chassisNumber" label="Chassis No" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                <Input placeholder="Chassis Number" prefix={<NumberOutlined />} onChange={handleInputChange} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Validity Dates - Compact */}
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
          <Row gutter={[16, 8]}>
            <Col xs={12} sm={8} md={6}>
              <Form.Item name="taxValidity" label="Tax" style={{ marginBottom: 8 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item name="fitnessValidity" label="Fitness" style={{ marginBottom: 8 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item name="insuranceValidity" label="Insurance" style={{ marginBottom: 8 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item name="pollutionValidity" label="Pollution" style={{ marginBottom: 8 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item name="npValidity" label="NP" style={{ marginBottom: 8 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item name="cgpValidity" label="CGP" style={{ marginBottom: 8 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Documents - Compact */}
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
          <Row gutter={[16, 8]}>
            <Col xs={12} sm={8} md={6}>
              <Form.Item label="RC Book" style={{ marginBottom: 8 }}>
                <Upload beforeUpload={() => false} onChange={(f) => handleFileChange('rcBook', f)} maxCount={1} accept=".pdf,.jpg,.png">
                  <Button size="small" icon={<CloudUploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item label="Insurance" style={{ marginBottom: 8 }}>
                <Upload beforeUpload={() => false} onChange={(f) => handleFileChange('insuranceDoc', f)} maxCount={1} accept=".pdf">
                  <Button size="small" icon={<CloudUploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item label="Pollution" style={{ marginBottom: 8 }}>
                <Upload beforeUpload={() => false} onChange={(f) => handleFileChange('pollutionDoc', f)} maxCount={1} accept=".pdf,.jpg">
                  <Button size="small" icon={<CloudUploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item label="TR Book" style={{ marginBottom: 8 }}>
                <Upload beforeUpload={() => false} onChange={(f) => handleFileChange('trBook', f)} maxCount={1} accept=".pdf,.jpg,.png">
                  <Button size="small" icon={<CloudUploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item label="Permit" style={{ marginBottom: 8 }}>
                <Upload beforeUpload={() => false} onChange={(f) => handleFileChange('permitDoc', f)} maxCount={1} accept=".pdf">
                  <Button size="small" icon={<CloudUploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item label="Fitness" style={{ marginBottom: 8 }}>
                <Upload beforeUpload={() => false} onChange={(f) => handleFileChange('fitnessDoc', f)} maxCount={1} accept=".pdf,.jpg">
                  <Button size="small" icon={<CloudUploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          {editMode && (existingFiles.rcBook || existingFiles.insuranceDoc) && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
              Existing files available - upload new to replace
            </Text>
          )}
        </Card>

        {/* Actions - Compact */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button size="middle" icon={<RollbackOutlined />} onClick={onBack}>
            {editMode ? 'Cancel' : 'Back'}
          </Button>
          {!editMode && (
            <Button size="middle" icon={<ClearOutlined />} onClick={clearForm} danger>
              Clear
            </Button>
          )}
          <Button type="primary" size="middle" icon={<SaveOutlined />} htmlType="submit" loading={loading}>
            {loading ? 'Saving...' : (editMode ? 'Update' : 'Save')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default NewInsurance;